import type { IDBPDatabase } from 'idb';
import { failure, fingerprint, revise, validCheck, validCommand, validId } from '../domain/daily-routine.ts';
import type { CheckView, CommitResult, DailyRoutineCheck, Result, SetCheckCommand } from '../domain/daily-routine.ts';
import type { DailyRoutineRepository } from '../ports/daily-routine-repository.ts';
import {
  openFenixDatabase,
  storageFailure,
  validDatasetControl,
} from './fenix-db.ts';

import type { FenixSchema } from './fenix-db.ts';

export type Fault = 'before-write' | 'after-head' | 'after-history' | 'after-receipt' | 'quota-before' | 'quota-after-history' | 'lost-ack';
// Synchronous injection only: no network/timer work while an IDB transaction is alive.
export class LabFaults {
  next: Fault | null = null;
  take(): Fault | null { const result = this.next; this.next = null; return result; }
}

function inject(fault: Fault | null, point: Fault) {
  if (fault === point) throw new DOMException('Synthetic G4 storage fault', point.startsWith('quota') ? 'QuotaExceededError' : 'AbortError');
}
export class IndexedDailyRoutine implements DailyRoutineRepository {
  private available = true;
  constructor(private readonly db: IDBPDatabase<FenixSchema>, private readonly faults: LabFaults) {
    db.addEventListener('versionchange', () => this.close());
    db.addEventListener('close', () => { this.available = false; });
  }
  close() { this.available = false; this.db.close(); }
  async readCheck(checkId: string): Promise<Result<CheckView>> {
    if (!this.available) return failure('STORAGE_UNAVAILABLE');
    if (!validId(checkId)) return failure('VALIDATION_ERROR');
    try {
      const tx = this.db.transaction(['control', 'heads', 'history'], 'readonly');
      const done = tx.done; void done.catch(() => {});
      const control = await tx.objectStore('control').get('control');
      if (!validDatasetControl(control)) { await done; return failure('CORRUPTION_DETECTED'); }
      if (control.restoreFence !== null) { await done; return failure('RESTORE_IN_PROGRESS'); }
      if (control.recoveryDatasetId !== null) { await done; return failure('CORRUPTION_DETECTED'); }
      const head = await tx.objectStore('heads').get([control.activeDatasetId, checkId]);
      if (!head) { await done; return failure('NOT_FOUND'); }
      if (!validCheck(head.check) || head.datasetId !== control.activeDatasetId || head.check.checkId !== checkId) {
        await done; return failure('CORRUPTION_DETECTED');
      }
      const historical = await tx.objectStore('history').get([control.activeDatasetId, checkId, head.check.revision]);
      await done;
      if (JSON.stringify(historical) !== JSON.stringify(head)) return failure('CORRUPTION_DETECTED');
      return { ok: true, value: { check: head.check, context: { datasetId: control.activeDatasetId, controlEpoch: control.controlEpoch } } };
    } catch (error) { return storageFailure(error); }
  }
  async setCheck(command: SetCheckCommand): Promise<Result<CommitResult>> {
    if (!this.available) return failure('STORAGE_UNAVAILABLE');
    if (!validCommand(command)) return failure('VALIDATION_ERROR');
    let tx;
    try {
      tx = this.db.transaction(['control', 'heads', 'history', 'receipts'], 'readwrite', { durability: 'strict' });
      const done = tx.done; void done.catch(() => {});
      const control = await tx.objectStore('control').get('control');
      const reject = async (code: Parameters<typeof failure>[0]) => { await done; return failure(code); };
      if (!validDatasetControl(control)) return await reject('CORRUPTION_DETECTED');
      if (control.restoreFence !== null) return await reject('RESTORE_IN_PROGRESS');
      if (control.recoveryDatasetId !== null) return await reject('CORRUPTION_DETECTED');
      if (control.activeDatasetId !== command.context.datasetId || control.controlEpoch !== command.context.controlEpoch)
        return await reject('DATASET_STALE');
      const prior = await tx.objectStore('receipts').get([control.activeDatasetId, command.operationId]);
      if (prior) {
        if (!validCheck(prior.check)) return await reject('CORRUPTION_DETECTED');
        if (prior.fingerprint !== fingerprint(command)) return await reject('INVARIANT_CONFLICT');
        const historical = await tx.objectStore('history').get([control.activeDatasetId, command.checkId, prior.check.revision]);
        if (!validCheck(prior.check) || prior.operationId !== command.operationId || prior.datasetId !== control.activeDatasetId
          || JSON.stringify(historical) !== JSON.stringify({ datasetId: prior.datasetId, check: prior.check }))
          return await reject('CORRUPTION_DETECTED');
        await done;
        return { ok: true, value: { status: 'REPLAYED', receipt: { operationId: prior.operationId, fingerprint: prior.fingerprint, check: prior.check } } };
      }
      const head = await tx.objectStore('heads').get([control.activeDatasetId, command.checkId]);
      if (!head) return await reject('NOT_FOUND');
      if (!validCheck(head.check) || head.datasetId !== control.activeDatasetId) return await reject('CORRUPTION_DETECTED');
      const historical = await tx.objectStore('history').get([control.activeDatasetId, command.checkId, head.check.revision]);
      if (JSON.stringify(historical) !== JSON.stringify(head)) return await reject('CORRUPTION_DETECTED');
      const next = revise(head.check, command);
      if (!next.ok) return await reject(next.code);
      if (!Number.isSafeInteger(control.datasetRevision + 1)) return await reject('INVARIANT_CONFLICT');
      const fault = this.faults.take();
      inject(fault, 'before-write'); inject(fault, 'quota-before');
      const nextHead = { datasetId: control.activeDatasetId, check: next.value };
      await tx.objectStore('heads').put(nextHead, [control.activeDatasetId, command.checkId]);
      inject(fault, 'after-head');
      await tx.objectStore('history').add(nextHead, [control.activeDatasetId, command.checkId, next.value.revision]);
      inject(fault, 'after-history'); inject(fault, 'quota-after-history');
      const receipt = { operationId: command.operationId, fingerprint: fingerprint(command), check: next.value };
      await tx.objectStore('receipts').add({ ...receipt, datasetId: control.activeDatasetId }, [control.activeDatasetId, command.operationId]);
      inject(fault, 'after-receipt');
      await tx.objectStore('control').put({ ...control, datasetRevision: control.datasetRevision + 1 });
      await done;
      if (fault === 'lost-ack') return failure('STORAGE_UNAVAILABLE');
      return { ok: true, value: { status: 'APPLIED', receipt } };
    } catch (error) {
      if (tx) { try { tx.abort(); } catch { /* already settled */ } await tx.done.catch(() => {}); }
      return storageFailure(error);
    }
  }
}
export async function openDailyRoutine(dbName: string, fixture: DailyRoutineCheck, datasetId: string, faults = new LabFaults()): Promise<Result<IndexedDailyRoutine>> {
  if (!validCheck(fixture) || !validId(datasetId)) return failure('VALIDATION_ERROR');
  let db: IDBPDatabase<FenixSchema> | undefined;
  try {
    const opened = await openFenixDatabase(dbName);
    if (!opened.ok) return opened;
    db = opened.value;
    const tx = db.transaction(['control', 'heads', 'history', 'receipts'], 'readwrite');
    const done = tx.done; void done.catch(() => {});
    const control = await tx.objectStore('control').get('control');
    if (!control) {
      const counts = await Promise.all((['control', 'heads', 'history', 'receipts'] as const).map(name => tx.objectStore(name).count()));
      if (counts.some(count => count !== 0)) { await done; db.close(); return failure('CORRUPTION_DETECTED'); }
      // Lab fixture bootstrap is outside React mount effects, atomic and idempotent across contexts.
      await tx.objectStore('control').add({ key: 'control', schemaVersion: 1, activeDatasetId: datasetId, controlEpoch: 0,
        datasetRevision: 0, recoveryDatasetId: null, restoreFence: null });
      const head = { datasetId, check: fixture };
      await tx.objectStore('heads').add(head, [datasetId, fixture.checkId]);
      await tx.objectStore('history').add(head, [datasetId, fixture.checkId, fixture.revision]);
    } else if (!validDatasetControl(control)) { await done; db.close(); return failure('CORRUPTION_DETECTED'); }
    await done;
    return { ok: true, value: new IndexedDailyRoutine(db, faults) };
  } catch (error) { db?.close(); return storageFailure(error); }
}
